import React, { type FC } from 'react';

import { useField } from 'formik';

import { Box } from '@clutter/clean';

import { Button, Table } from '@shared/components/bootstrap';
import { useToggle } from '@shared/hooks/use_toggle';

import {
  Dialog as ConfirmationDialog,
  Response as ConfirmationResponse,
  useConfirmation,
} from '@admin/components/confirmation';
import { Panel } from '@admin/components/helpers/panel';
import type { Landing__Location__BenefitInput } from '@admin/schema';

import { LandingLocationBenefitModal } from './modal';

const LandingLocationBenefitDeleteButton: FC<{
  benefit: Landing__Location__BenefitInput;
  onDelete(): void;
}> = ({ benefit, onDelete }) => {
  const { confirmation, confirm } = useConfirmation();
  const onClick = async () => {
    const confirmed = await confirm({
      title: <>Are you sure you want to delete {benefit.title}?</>,
      description: <>Deleting is permanent.</>,
    });
    if (confirmed !== ConfirmationResponse.Confirm) return;
    onDelete();
  };
  return (
    <>
      {confirmation && <ConfirmationDialog confirmation={confirmation} />}

      <Button kind="danger" onClick={onClick}>
        Delete
      </Button>
    </>
  );
};

const LandingLocationBenefitEditButton: FC<{
  benefit: Landing__Location__BenefitInput;
  onSave(_: Landing__Location__BenefitInput): void;
}> = ({ benefit, onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Edit
      </Button>
      {visible && (
        <LandingLocationBenefitModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
          value={benefit}
        />
      )}
    </>
  );
};

const LandingLocationBenefitNewButton: FC<{
  onSave(_: Landing__Location__BenefitInput): void;
}> = ({ onSave }) => {
  const { visible, onToggle } = useToggle();
  return (
    <>
      <Button kind="primary" onClick={() => onToggle()}>
        Add
      </Button>
      {visible && (
        <LandingLocationBenefitModal
          onSave={(input) => {
            onSave(input);
            onToggle();
          }}
          onClose={() => onToggle()}
        />
      )}
    </>
  );
};

export const LandingLocationBenefitsPanel: FC = () => {
  const [field, _, helpers] = useField<Array<Landing__Location__BenefitInput>>('benefits');

  return (
    <Panel>
      <Panel.Header>
        <Panel.Title>Benefits</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <Table striped>
          <thead>
            <tr>
              <th className="text-left">Title</th>
              <th className="text-left">Description</th>
              <th className="col-zero" />
            </tr>
          </thead>
          <tbody>
            {field.value.map(
              (benefit, index) =>
                !benefit._destroy && (
                  <tr key={index}>
                    <td className="text-left">{benefit.title}</td>
                    <td className="text-left">{benefit.description}</td>
                    <td className="col-zero">
                      <Box.Flex gap="4px">
                        <LandingLocationBenefitEditButton
                          benefit={benefit}
                          onSave={(input) => {
                            const benefits = [...field.value];
                            benefits[index] = {
                              ...benefits[index],
                              ...input,
                            };
                            helpers.setValue(benefits);
                          }}
                        />
                        <LandingLocationBenefitDeleteButton
                          benefit={benefit}
                          onDelete={() => {
                            const benefits = [...field.value];
                            benefits[index] = {
                              ...benefits[index],
                              _destroy: !benefits[index]._destroy,
                            };
                            helpers.setValue(benefits);
                          }}
                        />
                      </Box.Flex>
                    </td>
                  </tr>
                ),
            )}
          </tbody>
        </Table>
      </Panel.Body>
      <Panel.Footer align="right">
        <LandingLocationBenefitNewButton
          onSave={(input) => {
            const benefits = [...field.value];
            benefits.push(input);
            helpers.setValue(benefits);
          }}
        />
      </Panel.Footer>
    </Panel>
  );
};
